import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import './transactions.css';
import './receipts.css';

const ReceiptsPage = () => {
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [receipts, setReceipts] = useState([]);
    const [loadingProperties, setLoadingProperties] = useState(false);
    const [loadingReceipts, setLoadingReceipts] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [selectedPropertyId, setSelectedPropertyId] = useState('');
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [receiptForm, setReceiptForm] = useState({ propertyId: '' });
    const [message, setMessage] = useState({ type: '', text: '' });
    const [uploading, setUploading] = useState(false);
    const [lightboxSrc, setLightboxSrc] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const loadProperties = async () => {
        setLoadingProperties(true);
        setLoadError('');
        try {
            const res = await api.get('/property');
            const list = Array.isArray(res?.data) ? res.data : [];
            setProperties(list);
            if (list.length > 0 && !selectedPropertyId) {
                const firstId = list[0].id;
                setSelectedPropertyId(String(firstId));
                loadReceipts(firstId);
            }
        } catch (e) {
            console.error('Failed to load properties', e);
            setLoadError('Unable to load properties. Please refresh and try again.');
        } finally {
            setLoadingProperties(false);
        }
    };

    const loadReceipts = async (propertyId) => {
        if (!propertyId) return;
        setLoadingReceipts(true);
        setLoadError('');
        try {
            const res = await api.get(`/receipt?propertyId=${propertyId}`);
            setReceipts(Array.isArray(res?.data) ? res.data : []);
        } catch (e) {
            console.error('Failed to load receipts', e);
            setReceipts([]);
            setLoadError('Unable to load receipts for this property.');
        } finally {
            setLoadingReceipts(false);
        }
    };

    useEffect(() => {
        loadProperties();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePropertyChange = (e) => {
        const id = e.target.value;
        setSelectedPropertyId(id);
        setReceipts([]);
        loadReceipts(id);
    };

    const closeReceiptModal = () => {
        setReceiptForm({ propertyId: '' });
        setShowReceiptModal(false);
        setMessage({ type: '', text: '' });
    };

    const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const handleReceiptUpload = async (e) => {
        const file = e.target.files[0];
        // Reset input so the same file can be re-selected
        e.target.value = '';

        if (!receiptForm.propertyId) {
            setMessage({ type: 'error', text: 'Please select a property before uploading.' });
            return;
        }
        if (!file) return;

        setUploading(true);
        try {
            const fileData = await readFileAsBase64(file);
            await api.post('/receipt', {
                propertyId: receiptForm.propertyId,
                fileName: file.name,
                fileType: file.type,
                fileData
            });
            setShowReceiptModal(false);
            setReceiptForm({ propertyId: '' });
            setMessage({ type: 'success', text: 'Receipt uploaded successfully.' });
            // Reload receipts for the currently selected property
            if (receiptForm.propertyId === selectedPropertyId) {
                await loadReceipts(selectedPropertyId);
            }
        } catch (e) {
            console.error('Upload failed', e);
            setMessage({ type: 'error', text: 'Upload failed. Please try again.' });
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/receipt/${id}`);
            setConfirmDeleteId(null);
            setReceipts((prev) => prev.filter((r) => r.id !== id));
        } catch (e) {
            console.error('Delete failed', e);
        }
    };

    const selectedProperty = properties.find((p) => String(p.id) === String(selectedPropertyId));

    return (
        <div className="transactions-container">
            <div className="transactions-header">
                <div className="receipts-title-block">
                    <h2>Receipts</h2>
                    <p>Capture and organize receipt images by property.</p>
                </div>
                <div className="header-buttons">
                    <button className="btn-secondary" onClick={() => setShowReceiptModal(true)}>Upload Receipt</button>
                </div>
            </div>

            {message.text && (
                <div className={`app-notice ${message.type === 'success' ? 'app-notice-success' : 'app-notice-error'}`}>
                    {message.text}
                </div>
            )}

            {/* Property selector */}
            {loadingProperties ? (
                <div className="app-state-card app-state-info" role="status" aria-live="polite">
                    <p>Loading property list...</p>
                </div>
            ) : properties.length === 0 ? (
                <div className="app-state-card">
                    <p>No properties are available yet. Create your first property before uploading receipts.</p>
                    <div>
                        <button className="btn-primary" onClick={() => navigate('/Properties')}>Go to Properties</button>
                    </div>
                </div>
            ) : (
                <div className="receipts-property-bar">
                    <label htmlFor="receipts-property-select">View receipts for:</label>
                    <select id="receipts-property-select" value={selectedPropertyId} onChange={handlePropertyChange}>
                        <option value="">-- Choose a property --</option>
                        {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </div>
            )}

            {loadError && !loadingProperties && (
                <div className="app-state-card app-state-error" role="alert">
                    <p>{loadError}</p>
                    <div>
                        <button className="btn-primary" onClick={() => loadReceipts(selectedPropertyId)}>Retry</button>
                    </div>
                </div>
            )}

            {/* Receipt grid */}
            {selectedPropertyId && !loadError && (
                <div className="transactions-section">
                    <h3>{selectedProperty?.name || 'Property'} — Receipts ({receipts.length})</h3>
                    {loadingReceipts ? (
                        <div className="app-state-card app-state-info" role="status" aria-live="polite">
                            <p>Loading receipts...</p>
                        </div>
                    ) : receipts.length === 0 ? (
                        <p className="no-items">No receipts uploaded for this property yet.</p>
                    ) : (
                        <div className="receipts-grid">
                            {receipts.map((r) => (
                                <div className="receipt-thumb-card" key={r.id}>
                                    <img
                                        src={r.fileData}
                                        alt={`Receipt ${r.fileName}`}
                                        className="receipt-thumb"
                                        onClick={() => setLightboxSrc(r.fileData)}
                                        title="Click to enlarge"
                                    />
                                    <div className="receipt-thumb-footer">
                                        <span className="receipt-thumb-name" title={r.fileName}>{r.fileName}</span>
                                        <button
                                            className="receipt-thumb-delete"
                                            onClick={() => setConfirmDeleteId(r.id)}
                                            aria-label={`Delete receipt ${r.fileName}`}
                                            title="Delete"
                                        >✕</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Upload Modal */}
            {showReceiptModal && (
                <div className="modal-overlay" onClick={closeReceiptModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Upload Receipt</h3>
                            <button className="modal-close" aria-label="Close upload receipt dialog" onClick={closeReceiptModal}>&times;</button>
                        </div>
                        {message.text && (
                            <div className={`app-notice ${message.type === 'success' ? 'app-notice-success' : 'app-notice-error'}`}>
                                {message.text}
                            </div>
                        )}
                        <div className="receipt-upload">
                            <div className="receipt-property-select">
                                <label>Select Property:</label>
                                <select
                                    value={receiptForm.propertyId}
                                    onChange={(e) => setReceiptForm({ propertyId: e.target.value })}
                                >
                                    <option value="">-- Choose a property --</option>
                                    {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleReceiptUpload}
                                style={{ display: 'none' }}
                                id="receipt-file"
                                disabled={uploading}
                            />
                            <label htmlFor="receipt-file" className={`upload-label${uploading ? ' upload-label-disabled' : ''}`}>
                                <div className="upload-box">
                                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📸</div>
                                    <p>{uploading ? 'Uploading…' : 'Click to select an image'}</p>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            )}

            {/* Lightbox */}
            {lightboxSrc && (
                <div className="modal-overlay receipts-lightbox" onClick={() => setLightboxSrc(null)}>
                    <img src={lightboxSrc} alt="Receipt" className="receipts-lightbox-img" />
                </div>
            )}

            {/* Confirm Delete */}
            {confirmDeleteId && (
                <div className="modal-overlay" onClick={() => setConfirmDeleteId(null)}>
                    <div className="modal-content receipts-confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Delete Receipt?</h3>
                        <p>This cannot be undone.</p>
                        <div className="receipts-confirm-actions">
                            <button className="btn-ghost" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                            <button className="btn-danger" onClick={() => handleDelete(confirmDeleteId)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReceiptsPage;
